import { Trans } from '@lingui/react/macro';

import type { Permission } from '@/api';
import OrganizationSwitch from '@/components/OrganizationSwitch';
import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconCharts from '@/icons/Charts.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconHelp from '@/icons/Help.svg';
import IconInvoice from '@/icons/Invoice.svg';
import IconLogout from '@/icons/Logout.svg';
import IconSettings from '@/icons/Settings.svg';
import {
  getOrganizations,
  getUserWorkshops,
  hasPermission,
  hasPermissionOnAnyScope,
  useDeleteCurrentSession,
} from '@/models/user';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import useViewport from '@/utils/useViewport';

export const Menu = () => {
  const { isMobile } = useViewport();
  const { currentSession } = useCurrentSession();
  const [currentOrganization, setCurrentOrganization] = useCurrentOrganization();
  const {
    mutateAsync: deleteCurrentSession,
    isPending: isDeletingCurrentSession,
    isSuccess: hasDeletedCurrentSession,
  } = useDeleteCurrentSession();

  const canSeeSettings = useCanSeeSettings();
  const canViewInvoice = hasPermissionOnAnyScope(
    currentSession,
    'view_invoice',
    currentOrganization?.id
  );
  const canViewAnalytics = hasPermissionOnAnyScope(
    currentSession,
    'view_analytics',
    currentOrganization?.id
  );

  const showHelpAndFeedback =
    !currentOrganization || !!currentOrganization.config?.helpAndFeedback?.enabled;

  const name = currentSession?.name ?? '';

  // We want to have a specific height for all buttons of this screen, that's why we're using a
  // custom height and not the sizes offered by the Design System
  const buttonHeight = '56px';

  const organizations = getOrganizations(currentSession) ?? [];

  return (
    <>
      <div className="header">
        <Title size={isMobile ? '200' : '100'} as="h1">
          <Trans id="menu.title">Menu</Trans>
        </Title>
      </div>

      <div className="container">
        <Stack gap="24px">
          <Button
            to="/profile"
            variant="secondary"
            size="large"
            // We want to have a custom height for this one
            style={{ height: 'unset' }}
          >
            <Stack row alignItems="center" style={{ width: '100%' }} flexWrap="nowrap" gap="0.5rem">
              <UserAvatar user={currentSession} size="large" />
              <Stack alignItems="flex-start" style={{ minWidth: 0, flex: '1' }}>
                <InlineText weight="medium" ellipsis title={name} style={{ maxWidth: '100%' }}>
                  {name}
                </InlineText>
                <InlineText size="300">
                  <Trans id="menu.show-profile">Show profile</Trans>
                </InlineText>
              </Stack>
              <span>
                <IconChevron right />
              </span>
            </Stack>
          </Button>
          <Stack gap="0.5rem">
            {organizations.length > 1 && (
              <OrganizationSwitch
                organizations={getOrganizations(currentSession) ?? []}
                onSelectOrganization={setCurrentOrganization}
                placement="top left"
              >
                <Button
                  variant="secondary"
                  size="large"
                  className="is-hidden-from-tablet"
                  style={{ justifyContent: 'flex-start' }}
                >
                  <IconChevron down />
                  {currentOrganization?.name}
                </Button>
              </OrganizationSwitch>
            )}
            {canViewInvoice && (
              <Button
                to="/accounting"
                variant="secondary"
                size="medium"
                style={{ height: buttonHeight }}
              >
                <Stack
                  row
                  gap="0.5rem"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ width: '100%' }}
                >
                  <Stack row gap="0.5rem" alignItems="center">
                    <IconInvoice style={{ fontSize: '1.25rem' }} />
                    <Trans id="menu.accounting">Accounting</Trans>
                  </Stack>
                  <IconChevron right />
                </Stack>
              </Button>
            )}
            {canViewAnalytics && (
              <Button
                to="/analytics/dashboard"
                variant="secondary"
                size="medium"
                style={{ height: buttonHeight }}
              >
                <Stack
                  row
                  gap="0.5rem"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ width: '100%' }}
                >
                  <Stack row gap="0.5rem" alignItems="center">
                    <IconCharts style={{ fontSize: '1.25rem' }} />
                    <Trans id="menu.analytics">Analytics</Trans>
                  </Stack>
                  <IconChevron right />
                </Stack>
              </Button>
            )}
            {canSeeSettings && (
              <Button
                to="/settings"
                variant="secondary"
                size="medium"
                style={{ height: buttonHeight }}
              >
                <Stack
                  row
                  gap="0.5rem"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ width: '100%' }}
                >
                  <Stack row gap="0.5rem" alignItems="center">
                    <IconSettings style={{ fontSize: '1.25rem' }} />
                    <Trans id="menu.settings">Settings</Trans>
                  </Stack>
                  <IconChevron right />
                </Stack>
              </Button>
            )}
            {currentSession?.isSupport && (
              <Button
                to="/settings_beta"
                variant="secondary"
                size="medium"
                style={{ height: buttonHeight }}
              >
                <Stack
                  row
                  gap="0.5rem"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ width: '100%' }}
                >
                  <Stack row gap="0.5rem" alignItems="center">
                    <IconSettings style={{ fontSize: '1.25rem' }} />
                    <Trans id="menu.settings-beta">Settings (Beta)</Trans>
                  </Stack>
                  <IconChevron right />
                </Stack>
              </Button>
            )}
            {showHelpAndFeedback && (
              <Button to="/help" variant="secondary" size="medium" style={{ height: buttonHeight }}>
                <Stack
                  row
                  gap="0.5rem"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ width: '100%' }}
                >
                  <Stack row gap="0.5rem" alignItems="center">
                    <IconHelp style={{ fontSize: '1.25rem' }} />
                    <Trans id="menu.help-form">Help & Feedback</Trans>
                  </Stack>
                  <IconChevron right />
                </Stack>
              </Button>
            )}
          </Stack>
          <Button
            variant="secondary-danger"
            size="medium"
            onPress={() => {
              deleteCurrentSession();
            }}
            disabled={isDeletingCurrentSession || hasDeletedCurrentSession}
            style={{ height: buttonHeight }}
          >
            <Stack
              row
              gap="0.5rem"
              alignItems="center"
              justifyContent="space-between"
              style={{ width: '100%' }}
            >
              <Stack
                row
                gap="0.5rem"
                style={{ color: 'var(--color-danger-700)' }}
                alignItems="center"
              >
                <IconLogout style={{ fontSize: '1.25rem' }} />
                <Trans id="menu.log-out">Log out</Trans>
              </Stack>
              <IconChevron right />
            </Stack>
          </Button>
        </Stack>
      </div>
    </>
  );
};

export const useCanSeeSettings = () => {
  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  const canSeeOrganizationSettings = (
    [
      'manage_users',
      'manage_entities',
      'manage_organization',
      'manage_billing',
    ] satisfies Permission[]
  ).find((permission) =>
    hasPermission(currentSession, permission, {
      organizationId: currentOrganization?.id,
      entityId: null,
    })
  );

  const userWorkshops = getUserWorkshops(currentSession);
  const canSeeWorkshopSettings =
    userWorkshops.length === 1 &&
    hasPermission(currentSession, 'manage_entity', { entityId: userWorkshops[0].id });

  return canSeeOrganizationSettings || canSeeWorkshopSettings;
};
